import * as React from "react"

import { ItemRenderFunction, NavigatorProps } from "Catalog/Edit/shared/components/Navigator/types"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { CategoryId, ProductId } from "shared/hubrise/ids"
import { ICategoryOrProduct, isCategory } from "shared/hubrise/models"

import ItemCategory from "./Item/ItemCategory"
import ItemProduct from "./Item/ItemProduct"
import { StyledProductNavigator } from "./Styles"
import { moveCategoryOrProduct } from "./utils"

interface CategoriesProps {
  items: Array<ICategoryOrProduct>
  selectedId?: CategoryId | ProductId
}

const Navigator = ({ items, selectedId }: CategoriesProps): JSX.Element => {
  const navigate = useNavigateNoUpdate()
  const { editor } = useEditor()
  const { catalogNavigation } = useCatalogNavigation()

  const selectItem = React.useCallback(
    (categoryOrProduct: ICategoryOrProduct): void => {
      navigate(
        isCategory(categoryOrProduct)
          ? catalogNavigation.editPath("category", categoryOrProduct.id)
          : catalogNavigation.editPath("product", categoryOrProduct.id),
      )
    },
    [catalogNavigation, navigate],
  )

  const moveItem: NavigatorProps<ICategoryOrProduct>["moveItem"] = React.useCallback(
    (item, newPosition) => moveCategoryOrProduct(editor, items, item, newPosition),
    [editor, items],
  )

  const renderItem: ItemRenderFunction<ICategoryOrProduct> = React.useCallback((item, itemRenderProps) => {
    if (isCategory(item)) {
      return <ItemCategory key={item.id} category={item} itemRenderProps={itemRenderProps} />
    } else {
      return <ItemProduct key={item.id} product={item} itemRenderProps={itemRenderProps} />
    }
  }, [])

  return <StyledProductNavigator {...{ items, selectItem, selectedId, moveItem, renderItem }} />
}

export default Navigator
